import { css } from '@emotion/react';
import { EventNote } from '@mui/icons-material';
import { Avatar, Box } from '@mui/material';
import { Fragment, useMemo } from 'react';

import { processFilestackUrlSrcSet } from '@jebel/utils';

import { EventDetailsModal } from 'features/events/components/Events/EventInfoModal';
import { Typography } from 'shared/components/ui';
import { EventInfoFragment } from 'shared/graphql';
import { useModalState } from 'shared/hooks';
import { getFullMonthAndWeekdayAndTime } from 'shared/utils/date';

const AVATAR_ICON_SIZE = 80;
const DEFAULT_TIMEZONE = 'CST';

const containerCSS = css`
  display: flex;
  gap: 1rem;
  cursor: pointer;
  user-select: none;
`;

const avatarCSS = css`
  width: ${AVATAR_ICON_SIZE}px;
  height: ${AVATAR_ICON_SIZE}px;
  font-size: ${AVATAR_ICON_SIZE * 0.6}px;
`;

const contentCSS = css`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export function EventCard({ event }: { event: EventInfoFragment }) {
  const { show: isModalOpen, open: openModal, close: closeModal } = useModalState();

  const imageSource = useMemo(() => {
    return event?.images?.items?.[0]?.downloadUrl ?? undefined;
  }, [event.images]);

  return (
    <Fragment>
      {isModalOpen && <EventDetailsModal show={isModalOpen} event={event} onClose={closeModal} />}

      <Box css={containerCSS} onClick={openModal}>
        <Avatar
          variant="rounded"
          src={imageSource}
          alt={event.title!}
          css={avatarCSS}
          srcSet={processFilestackUrlSrcSet(imageSource, {
            compress: true,
            resize: {
              width: AVATAR_ICON_SIZE,
              height: AVATAR_ICON_SIZE,
              fit: 'crop',
            },
          })}
        >
          <EventNote fontSize="inherit" />
        </Avatar>

        <Box css={contentCSS}>
          <Typography>{event.title}</Typography>

          <Typography variant="body2" color="textSecondary">
            {getFullMonthAndWeekdayAndTime(event?.date, event?.timezone || DEFAULT_TIMEZONE)}
          </Typography>
        </Box>
      </Box>
    </Fragment>
  );
}
